import React, { useMemo } from "react";

import { FlightPrefillFields } from "Bands/types";
import { FormBuilder } from "shared/components";
import { StyledFieldset } from "shared/components/Form/Fieldset/styles";
import { space } from "shared/styles/constants";

import { prefillFlight } from "Bands/state";
import { useDispatch } from "react-redux";
import { copy } from "shared/assets";
import { addTemporaryToast } from "shared/components/Toast";
import { IAPIState, IJSONAPIErrorCodes, IStatus, StateErrorType } from "shared/types";
import { getPrefillFields } from "./formData";

import { loggerService } from "shared/services";
import { createNewDate, isoToUTCString } from "shared/utils";
import { useEventContext } from "Bands/contexts";
import { format } from "date-fns";

type Props = {
  apiMeta?: {
    status: IAPIState;
    error?: StateErrorType;
  };
};

export const EventFlightTravelLegPrefillForm: React.FC<Props> = ({ apiMeta }) => {
  const dispatch = useDispatch();
  const { event } = useEventContext();

  const onSubmit = async (data: FlightPrefillFields) => {
    let message = {
      status: IStatus.success,
      message: copy.travelBlock.flight.prefill.messages.success,
    };

    try {
      const originalPromiseResult = await dispatch(
        prefillFlight(data)
        // @ts-ignore
      ).unwrap();

      const prefillFlightsFound = Object.values(
        originalPromiseResult?.scheduledFlight || {}
      ).length;

      if (!prefillFlightsFound) {
        message = {
          status: IStatus.info,
          message: copy.travelBlock.flight.prefill.messages.empty,
        };
      }
    } catch (e) {
      loggerService.log({
        icon: "❌",
        title: "Failed to prefill flight:",
        body: e,
      });

      message = {
        status: IStatus.error,
        message: copy.travelBlock.flight.prefill.messages.error,
      };
    }

    dispatch(addTemporaryToast(message));
  };

  const defaultDate = useMemo(() => {
    if (!event?.date) return null;

    const today = createNewDate();

    const eventDate = createNewDate(
      isoToUTCString({
        iso: event?.date,
      })
    );

    return format(eventDate < today ? today : eventDate, "yyyy-MM-dd");
  }, [event?.date]);

  const defaultValues = {
    prefillFlightNumber: null,
    prefillFlightDate: defaultDate,
  };

  const error = useMemo(() => {
    const { errorCodes } = apiMeta?.error || {};

    const messages = copy.travelBlock.flight.errors;

    if (errorCodes?.includes(IJSONAPIErrorCodes.InvalidFlightNumber)) {
      return messages.invalidFlightNumber;
    }

    if (errorCodes?.includes(IJSONAPIErrorCodes.InvalidCarrier)) {
      return messages.invalidCarrier;
    }

    if (errorCodes?.includes(IJSONAPIErrorCodes.InvalidDepartureDate)) {
      return messages.invalidDepartureDate;
    }

    return !!apiMeta?.error;
  }, [apiMeta]);

  return (
    <StyledFieldset as="div" style={{ marginBottom: space.s }}>
      <FormBuilder
        fields={getPrefillFields()}
        onSubmit={onSubmit}
        apiErrorMessage={error}
        neverGoBackToDefaultValues
        defaultValues={defaultValues}
        isSubmitting={apiMeta?.status === IAPIState.pending}
        submitMode="isDirty"
        style={{ gridTemplateColumns: "repeat(6, 1fr)", marginTop: space.s }}
        buttons={{
          primary: {
            children: "Search and prefill flight",
          },
        }}
      />
    </StyledFieldset>
  );
};
